import React, { useState, useEffect } from 'react';
import { get, post, billingPost, getChannelCatalog, importChannelCatalog, linkEbaySkus, getEbaySettingsOptions, getEtsySettingsOptions, searchEtsyTaxonomy } from './api';
import { getApiKey } from '../databaseManager';
import { formattedDate } from './sharedUtils';
import { useAuth } from './authContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ApiKeyField from './apiKeyField';
import { doc, getDoc, setDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase'; // Import your Firebase configuration
import { Paper, Typography, Grid, TextField, Checkbox, FormControlLabel, Button, Container, MenuItem, Select, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, List, ListItem, ListItemAvatar, ListItemText, Avatar, Chip, Alert, Accordion, AccordionSummary, AccordionDetails, Box, Stack, Autocomplete } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedIcon from '@mui/icons-material/Verified';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SellIcon from '@mui/icons-material/Sell';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import SyncIcon from '@mui/icons-material/Sync';

// Shared visual language for the settings page — section headers with an icon
// badge + title + subtitle, and pill-shaped buttons, all in Hemlock's palette.
const SectionHeader = ({ icon, title, subtitle }) => (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Box sx={{
            width: 44, height: 44, borderRadius: '50%',
            bgcolor: 'rgba(71, 81, 237, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'primary.main', flexShrink: 0,
        }}>
            {icon}
        </Box>
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{title}</Typography>
            {subtitle && (
                <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
            )}
        </Box>
    </Stack>
);

const sectionPaperSx = { padding: 24, marginBottom: 20, borderRadius: 16, border: '1px solid #ECEDF3', boxShadow: 'none' };

const pillButtonSx = {
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
    ':hover': { boxShadow: 'none' },
};

const primaryPillSx = {
    ...pillButtonSx,
    backgroundColor: 'primary.main',
    color: 'white',
    ':hover': { backgroundColor: 'secondary.main', boxShadow: 'none' },
};

const outlinePillSx = {
    ...pillButtonSx,
    backgroundColor: 'white',
    color: 'primary.main',
    borderColor: 'primary.main',
    ':hover': { backgroundColor: 'primary.main', color: 'white', borderColor: 'secondary.main' },
};

const dangerOutlinePillSx = {
    ...pillButtonSx,
    backgroundColor: 'white',
    color: '#c62828',
    borderColor: '#e0a1a1',
    ':hover': { backgroundColor: '#fdecea', color: '#c62828', borderColor: '#c62828' },
};

const ConnectedChip = ({ connected, connectedLabel = 'Connected', notConnectedLabel = 'Not connected' }) => (
    <Chip
        size="small"
        icon={connected ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : undefined}
        label={connected ? connectedLabel : notConnectedLabel}
        sx={{
            fontWeight: 600,
            bgcolor: connected ? 'rgba(46, 125, 50, 0.1)' : 'rgba(0,0,0,0.06)',
            color: connected ? '#2e7d32' : 'text.secondary',
        }}
    />
);

// Icon + accent color for each channel's avatar badge — kept small and out of the
// way so button/action colors can stay consistently Shopik purple.
const CHANNEL_META = {
    shopify: { icon: <StorefrontIcon />, color: '#95BF47' },
    ebay: { icon: <SellIcon />, color: '#E53238' },
    google: { icon: <ShoppingBagIcon />, color: '#4285F4' },
    etsy: { icon: <LocalOfferIcon />, color: '#F1641E' },
    square: { icon: <CropSquareIcon />, color: '#006aff' },
};

const ChannelAvatar = ({ channel }) => {
    const meta = CHANNEL_META[channel] || {};
    return (
        <Avatar sx={{ bgcolor: `${meta.color}1A`, color: meta.color, width: 40, height: 40 }}>
            {meta.icon}
        </Avatar>
    );
};

// eBay's redirect_uri is a fixed "RuName" whose accepted URL always lands back on the
// shopik-next-webapp host (shopik.io / shopik-next-stage.web.app). Starting the flow from
// that same origin (as the mobile app does) keeps the localStorage handoff same-origin;
// starting it from dashboard.shopik.io leaves the callback unable to read it back.
const getEbayAuthUrl = (customerId, destinationId) => {
    const host = process.env.REACT_APP_PROJECT_ID === 'shopik-production'
        ? 'https://shopik.io/'
        : 'https://shopik-next-stage.web.app/';
    return `${host}ebay-auth/${customerId}/${destinationId}`;
};

// Labels for the generic catalog-import dialog; add an entry here (and a matching
// backend adapter in core/catalog_import_utils.py) to support a new channel.
const CATALOG_CHANNEL_LABELS = { square: 'Square', ebay: 'eBay' };

// Etsy's who_made/when_made listing enums are fixed by Etsy's API — no fetch needed.
const ETSY_WHO_MADE_OPTIONS = [
    { value: 'i_did', label: 'I did' },
    { value: 'someone_else', label: 'Someone else' },
    { value: 'collective', label: 'A member of my shop' },
];
const ETSY_WHEN_MADE_OPTIONS = [
    { value: 'made_to_order', label: 'Made to order' },
    { value: '2020_2026', label: '2020–2026' },
    { value: '2010_2019', label: '2010–2019' },
    { value: '2006_2009', label: '2006–2009' },
    { value: 'before_2006', label: 'Before 2006' },
];

const Settings = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, customer, shopikUser } = useAuth();
    const [apiKey, setApiKey] = useState(''); // API key state
    const [dataChanged, setDataChanged] = useState(false);
    const [gmcConnecting, setGmcConnecting] = useState(false);
    const [gmcConnectError, setGmcConnectError] = useState(null);
    const gmcStatus = searchParams.get('gmc'); // 'connected', 'denied', 'error', 'no_merchant'
    const [etsyConnecting, setEtsyConnecting] = useState(false);
    const [etsyConnectError, setEtsyConnectError] = useState(null);
    const etsyStatus = searchParams.get('etsy'); // 'connected', 'denied', 'error'
    const [ebayLinking, setEbayLinking] = useState(false);
    const [ebayLinkResult, setEbayLinkResult] = useState(null);
    // eBay listing settings options (fulfillment/payment/return policies, locations),
    // keyed by destination id — mirrors the picker the iOS app has for setting up
    // destination.ebaySettings, which the web app previously had no way to populate.
    const [ebaySettingsOptions, setEbaySettingsOptions] = useState({});
    // Etsy shipping profiles & return policies, keyed by destination id — same
    // "web previously had no way to populate this" gap as eBay above.
    const [etsySettingsOptions, setEtsySettingsOptions] = useState({});
    // Etsy taxonomy autocomplete state, keyed by destination id: { query, results, loading }
    const [etsyTaxonomySearch, setEtsyTaxonomySearch] = useState({});
    const etsyTaxonomyDebounceRef = React.useRef({});
    const [userData, setUserData] = useState({
        name: '',
        phone: '',
    });
    const [customerData, setCustomerData] = useState({
        // email: '',
        customSkuFormat: ''
    });
    const [squareSettings, setSquareSettings] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [catalogChannel, setCatalogChannel] = useState(null); // 'square' | 'ebay' | future channels
    const [catalogOpen, setCatalogOpen] = useState(false);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [catalogItems, setCatalogItems] = useState([]);
    const [catalogError, setCatalogError] = useState(null);
    const [selectedItemIds, setSelectedItemIds] = useState(new Set());
    const [catalogPageSize, setCatalogPageSize] = useState(10);
    const [catalogHasMore, setCatalogHasMore] = useState(false);
    const [catalogTotal, setCatalogTotal] = useState(0);
    const [catalogRangeStart, setCatalogRangeStart] = useState(0);
    const [catalogRangeEnd, setCatalogRangeEnd] = useState(0);
    const [catalogNextCursor, setCatalogNextCursor] = useState(null);
    const [catalogCurrentCursor, setCatalogCurrentCursor] = useState(null); // cursor used to load the current page
    const [catalogCursorStack, setCatalogCursorStack] = useState([]); // cursors for back navigation
    const [catalogApiKey, setCatalogApiKey] = useState(null);
    const [catalogSearch, setCatalogSearch] = useState('');
    const [catalogSearchInput, setCatalogSearchInput] = useState('');

    const loadCatalogPage = async (cursor, pageSize, apiKey, query = catalogSearch, channel = catalogChannel) => {
        setCatalogLoading(true);
        setCatalogError(null);
        try {
            const key = apiKey ?? catalogApiKey ?? await getApiKey(customer.customerId);
            if (!catalogApiKey) setCatalogApiKey(key);
            const result = await getChannelCatalog(channel, key, { limit: pageSize, cursor: cursor ?? null, query });
            if (result.error) throw new Error(result.error);
            setCatalogItems(result.items || []);
            setCatalogHasMore(result.has_more || false);
            setCatalogNextCursor(result.next_cursor || null);
            setCatalogCurrentCursor(cursor ?? null);
            setCatalogTotal(result.total || 0);
            setCatalogRangeStart(result.range_start || 0);
            setCatalogRangeEnd(result.range_end || 0);
        } catch (e) {
            setCatalogError(e.message);
        } finally {
            setCatalogLoading(false);
        }
    };

    // Opens the catalog-import dialog for a given channel ('square', 'ebay', ...) and loads page 1.
    const openCatalogDialog = async (channel) => {
        setCatalogChannel(channel);
        setCatalogOpen(true);
        setCatalogItems([]);
        setSelectedItemIds(new Set());
        setImportResult(null);
        setCatalogCursorStack([]);
        setCatalogNextCursor(null);
        setCatalogCurrentCursor(null);
        setCatalogTotal(0);
        setCatalogRangeStart(0);
        setCatalogRangeEnd(0);
        setCatalogSearch('');
        setCatalogSearchInput('');
        setCatalogApiKey(null);
        const key = await getApiKey(customer.customerId);
        setCatalogApiKey(key);
        loadCatalogPage(null, catalogPageSize, key, '', channel);
    };

    // Debounce search — reset to page 1 when search term changes
    useEffect(() => {
        if (!catalogOpen || !catalogApiKey) return;
        const timer = setTimeout(() => {
            setCatalogSearch(catalogSearchInput);
            setCatalogCursorStack([]);
            setCatalogCurrentCursor(null);
            loadCatalogPage(null, catalogPageSize, catalogApiKey, catalogSearchInput);
        }, 400);
        return () => clearTimeout(timer);
    }, [catalogSearchInput]); // eslint-disable-line react-hooks/exhaustive-deps

     // Initial state for the destinations array
    const [destinations, setDestinations] = useState([
    ]);

    // Firestore stores this field as "isEnabled"; in app code we use the clearer
    // "autoPostEnabled" name and translate at the Firestore read/write boundary.
    const toLocalDestination = (d) => {
        const { isEnabled, ...rest } = d;
        return { ...rest, autoPostEnabled: !!isEnabled };
    };
    const toStoredDestination = (d) => {
        const { autoPostEnabled, ...rest } = d;
        return { ...rest, isEnabled: !!autoPostEnabled };
    };

    // Handle change in any destination field
    const handleDestinationChange = (index, field, value) => {
        const updatedDestinations = destinations.map((destination, i) =>
        i === index ? { ...destination, [field]: value } : destination
        );
        setDestinations(updatedDestinations);
        setCustomerData((prevData) => ({
            ...prevData,
            destinations: updatedDestinations
        }));
        setDataChanged(true)
    };

    // Square doesn't use storeId/storeKey (credentials live on squareSettings) — its
    // destination entry exists only to carry the auto-post/postToDraft toggles.
    const squareDestinationIndex = destinations.findIndex(d => d.type === 4);
    const squareDestination = squareDestinationIndex >= 0 ? destinations[squareDestinationIndex] : null;
    const handleSquareDestinationChange = (field, value) => {
        if (squareDestinationIndex >= 0) {
            handleDestinationChange(squareDestinationIndex, field, value);
            return;
        }
        const updatedDestinations = [
            ...destinations,
            {
                id: "",
                includeDescription: true,
                autoPostEnabled: false,
                postToDraft: true,
                storeId: "",
                storeKey: "",
                type: 4,
                [field]: value,
            },
        ];
        setDestinations(updatedDestinations);
        setCustomerData((prevData) => ({
            ...prevData,
            destinations: updatedDestinations
        }));
        setDataChanged(true);
    };

    // Add new destination
    const addNewDestination = () => {
        setDestinations([
        ...destinations,
        {
            id: "",
            includeDescription: true,
            autoPostEnabled: false,
            postToDraft: true,
            storeId: "",
            storeKey: "",
            type: 1,
        },
        ]);
        setDataChanged(true)
    }; 
    // Remove destination by index
    const removeDestination = (indexToRemove) => {
        setDestinations((prevDestinations) =>
        prevDestinations.filter((_, index) => index !== indexToRemove)
        );
        console.log(destinations)
        setDataChanged(true);
    };

    const connectGoogleMerchant = async () => {
        setGmcConnecting(true);
        setGmcConnectError(null);
        try {
            const key = await getApiKey(customer.customerId);
            const result = await get('/google-merchant/connect', key);
            if (result.authUrl) {
                window.location.href = result.authUrl;
            } else {
                setGmcConnectError('No auth URL returned from the server.');
            }
        } catch (e) {
            console.error('Failed to get GMC auth URL', e);
            setGmcConnectError(e.response?.data?.error || e.message || 'Failed to start Google Merchant connection.');
        } finally {
            setGmcConnecting(false);
        }
    };

    const connectEtsy = async () => {
        setEtsyConnecting(true);
        setEtsyConnectError(null);
        try {
            const key = await getApiKey(customer.customerId);
            const result = await get('/etsy/connect', key);
            if (result.authUrl) {
                window.location.href = result.authUrl;
            } else {
                setEtsyConnectError('No auth URL returned from the server.');
            }
        } catch (e) {
            console.error('Failed to get Etsy auth URL', e);
            setEtsyConnectError(e.response?.data?.error || e.message || 'Failed to start Etsy connection.');
        } finally {
            setEtsyConnecting(false);
        }
    };

    const addEbayIntegration = async () => {
        const destId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();
        const newDest = {
            id: destId,
            type: 2,
            storeId: '',
            storeKey: '',
            includeDescription: true,
            postToDraft: true,
            autoPostEnabled: false,
        };
        const newDestinations = [...destinations, newDest];
        if (customer && customer.customerId) {
            const custDoc = doc(db, 'customers', customer.customerId);
            await setDoc(custDoc, { destinations: newDestinations.map(toStoredDestination) }, { merge: true });
        }
        setDestinations(newDestinations);
        window.location.href = getEbayAuthUrl(customer.customerId, destId);
    };

    const loadEbaySettingsOptions = async (destId) => {
        setEbaySettingsOptions((prev) => ({
            ...prev,
            [destId]: { ...(prev[destId] || {}), loading: true, error: null },
        }));
        try {
            const key = apiKey || await getApiKey(customer.customerId);
            const result = await getEbaySettingsOptions(key);
            setEbaySettingsOptions((prev) => ({
                ...prev,
                [destId]: {
                    loading: false,
                    error: null,
                    loaded: true,
                    fulfillmentPolicies: result.fulfillmentPolicies || [],
                    paymentPolicies: result.paymentPolicies || [],
                    returnPolicies: result.returnPolicies || [],
                    locations: result.locations || [],
                },
            }));
        } catch (e) {
            setEbaySettingsOptions((prev) => ({
                ...prev,
                [destId]: {
                    ...(prev[destId] || {}),
                    loading: false,
                    error: e.response?.data?.error || e.message || 'Failed to load eBay settings options.',
                },
            }));
        }
    };

    const updateEbaySetting = (index, field, value) => {
        const current = destinations[index].ebaySettings || {};
        handleDestinationChange(index, 'ebaySettings', { ...current, [field]: value });
    };

    const loadEtsySettingsOptions = async (destId) => {
        setEtsySettingsOptions((prev) => ({
            ...prev,
            [destId]: { ...(prev[destId] || {}), loading: true, error: null },
        }));
        try {
            const key = apiKey || await getApiKey(customer.customerId);
            const result = await getEtsySettingsOptions(key);
            setEtsySettingsOptions((prev) => ({
                ...prev,
                [destId]: {
                    loading: false,
                    error: null,
                    loaded: true,
                    shippingProfiles: result.shippingProfiles || [],
                    returnPolicies: result.returnPolicies || [],
                },
            }));
        } catch (e) {
            setEtsySettingsOptions((prev) => ({
                ...prev,
                [destId]: {
                    ...(prev[destId] || {}),
                    loading: false,
                    error: e.response?.data?.error || e.message || 'Failed to load Etsy settings options.',
                },
            }));
        }
    };

    const updateEtsySetting = (index, field, value) => {
        const current = destinations[index].etsySettings || {};
        handleDestinationChange(index, 'etsySettings', { ...current, [field]: value });
    };

    // Debounced substring search against Etsy's seller taxonomy tree — the tree
    // has ~10k nodes, so it's searched server-side rather than shipped whole.
    const searchEtsyTaxonomyFor = (destId, query) => {
        setEtsyTaxonomySearch((prev) => ({
            ...prev,
            [destId]: { ...(prev[destId] || {}), query, loading: true },
        }));
        clearTimeout(etsyTaxonomyDebounceRef.current[destId]);
        etsyTaxonomyDebounceRef.current[destId] = setTimeout(async () => {
            try {
                const key = apiKey || await getApiKey(customer.customerId);
                const result = await searchEtsyTaxonomy(key, query);
                setEtsyTaxonomySearch((prev) => ({
                    ...prev,
                    [destId]: { ...(prev[destId] || {}), loading: false, results: result.results || [] },
                }));
            } catch (e) {
                setEtsyTaxonomySearch((prev) => ({
                    ...prev,
                    [destId]: { ...(prev[destId] || {}), loading: false, results: [] },
                }));
            }
        }, 400);
    };

    const handleLinkEbaySkus = async () => {
        setEbayLinking(true);
        setEbayLinkResult(null);
        try {
            const key = apiKey || await getApiKey(customer.customerId);
            const result = await linkEbaySkus(key);
            setEbayLinkResult({ success: true, linked: result.linked || [] });
        } catch (e) {
            setEbayLinkResult({
                success: false,
                error: e.response?.data?.error || e.message || 'Failed to link eBay SKUs.',
            });
        } finally {
            setEbayLinking(false);
        }
    };

    useEffect(() => {
        // Fetch user data and API key when component mounts
        if (customer) {
            setUserData({
                name: shopikUser.name || '',
                phone: shopikUser.phone || '',
            });
            setCustomerData({
                customSkuFormat: customer.customSkuFormat || "",
                inventorySyncEnabled: !!customer.inventorySyncEnabled,
                useExperimentalFeatures: !!customer.useExperimentalFeatures,
            })
            setDestinations((customer.destinations || []).map(toLocalDestination))
            setApiKey(customer.apiKey)
            setSquareSettings(customer.squareSettings || null)
        }

    }, [customer]);
    
    // Update user data
    const handleUserDataChange = (e) => {
        const { name, value } = e.target;
        setUserData((prevData) => ({
            ...prevData,
            [name]: value
        }));
        setDataChanged(true)
    };

    // Update user data
    const handleCustomerDataChange = (e) => {
        const { name, value } = e.target;
        setCustomerData((prevData) => ({
            ...prevData,
            customSkuFormat: value
        }));
        setDataChanged(true)
    };

    // Save user data to Firebase
    const cancelSave = async () => {
        if (customer) {
            setUserData({
                name: shopikUser.name || '',
                phone: shopikUser.phone || '',
            });
            setCustomerData({
                customSkuFormat: customer.customSkuFormat || "",
                inventorySyncEnabled: !!customer.inventorySyncEnabled,
                useExperimentalFeatures: !!customer.useExperimentalFeatures,
            })
            setDestinations((customer.destinations || []).map(toLocalDestination))
            setApiKey(customer.apiKey)
            setSquareSettings(customer.squareSettings || null)
            setDataChanged(false)
        }
    };

    // Save user data to Firebase
    const saveUserData = async () => {
        try {
            // console.log(customer.id + ":" + user.id + ":" + shopikUser)
            if (customer && customer.id && shopikUser.userId) {
                // The signed-in user's own doc — not customer.id, which for team
                // members is the account owner's (and Firestore rules deny it).
                const userDoc = doc(db, 'users', shopikUser.userId);
                await setDoc(userDoc, userData, { merge: true }); // Update the document, merging fields if it exists
                if (customer.customerId === shopikUser.userId) {
                    await saveCustomerData()
                }
                setDataChanged(false)
            } else {
                console.error('Customer ID not found. Cannot save user data.');
            }
        } catch (error) {
            console.error('Error updating user data:', error);
        }
    };
    
    // Save user data to Firebase
    const saveCustomerData = async () => {
        try {
            // console.log(customer.id + ":" + user.id + ":" + shopikUser)
            if (customer && customer.customerId) { // Ensure customer has an id
                const userDoc = doc(db, 'customers', customer.customerId); // Reference to the user's document in Firestore
                var newData = customerData
                // `customer` (and this component's `destinations` state derived from it)
                // is loaded once via a one-time getDoc, not a live listener — if a
                // destination was connected/updated elsewhere since this tab loaded
                // (e.g. the eBay OAuth callback page, or another tab), this tab's local
                // copy is stale. Re-read the current destinations right before writing
                // and carry over accessToken/storeKey from the server whenever this
                // tab's local copy for that destination is blank, so an unrelated save
                // (like a name change) can never silently wipe out a token this tab
                // never knew about.
                let freshDestinationsById = {};
                try {
                    const freshSnap = await getDoc(userDoc);
                    for (const d of (freshSnap.data()?.destinations || [])) {
                        freshDestinationsById[d.id] = d;
                    }
                } catch (e) {
                    console.error('saveCustomerData: failed to re-read destinations before save', e);
                }
                newData.destinations = destinations.map(toStoredDestination).map((d) => {
                    const fresh = freshDestinationsById[d.id];
                    if (!fresh) return d;
                    return {
                        ...d,
                        accessToken: d.accessToken || fresh.accessToken,
                        storeKey: d.storeKey || fresh.storeKey,
                    };
                });
                newData.squareSettings = squareSettings ?? deleteField();
                await setDoc(userDoc, newData, { merge: true }); // Update the document, merging fields if it exists
            } else {
                console.error('Customer ID not found. Cannot save customer data.');
            }
        } catch (error) {
            console.error('Error updating customer data:', error);
        }
    };
    
    const [manageSubscriptionLoading, setManageSubscriptionLoading] = useState(false);
    const manageSubscription = async () => {
        if (!customer || !customer.stripeCustomerId) return;
        setManageSubscriptionLoading(true);
        try {
            const session = await billingPost('/create-billing-portal-session', {
                shopikCustomerId: customer.id,
                customerId: customer.stripeCustomerId,
            });
            if (session.url) {
                window.location.href = session.url;
            }
        } catch (error) {
            console.error('Error creating billing portal session:', error);
        } finally {
            setManageSubscriptionLoading(false);
        }
    };

    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [cancelError, setCancelError] = useState(null);
    const [cancelSuccess, setCancelSuccess] = useState(false);
    const cancelSubscription = async () => {
        if (!customer || !customer.tierSubscription || !customer.tierSubscription.stripeSubscriptionId) return;
        setCancelLoading(true);
        setCancelError(null);
        try {
            await billingPost('/cancel-subscription', {
                subscriptionId: customer.tierSubscription.stripeSubscriptionId,
            });
            setCancelSuccess(true);
            setCancelDialogOpen(false);
        } catch (error) {
            console.error('Error canceling subscription:', error);
            setCancelError('Could not cancel your subscription. Please try again or contact support.');
        } finally {
            setCancelLoading(false);
        }
    };

    // Regenerate API Key
    const regenerateApiKey = async () => {
        try {
            const response = await post('/regenerate-api-key'); // Replace with actual API endpoint
            setApiKey(response.apiKey);
            alert('API key regenerated successfully!');
        } catch (error) {
            console.error('Error regenerating API key:', error);
        }
    };

    return (
        <><Container maxWidth="md" sx={{ py: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Settings</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Manage your account and integrations
            </Typography>
            <Paper style={sectionPaperSx}>
                <SectionHeader
                    icon={<PersonOutlineIcon />}
                    title="Account Information"
                    subtitle="Update your personal details"
                />
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Name"
                            variant="outlined"
                            fullWidth
                            name="name"
                            value={userData.name}
                            onChange={handleUserDataChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Email"
                            variant="outlined"
                            fullWidth
                            value={user?.email || ''}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <Chip
                                        size="small"
                                        icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
                                        label="Verified"
                                        sx={{ bgcolor: 'rgba(46, 125, 50, 0.1)', color: '#2e7d32', fontWeight: 600 }}
                                    />
                                ),
                            }}
                            helperText="Account email cannot be changed here"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Phone Number"
                            variant="outlined"
                            fullWidth
                            name="phone"
                            value={userData.phone}
                            onChange={handleUserDataChange}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {(customer.customerId === shopikUser.userId) && (
            <>
            <Paper style={sectionPaperSx}>
                <SectionHeader
                    icon={<LinkIcon />}
                    title="Connected Services"
                    subtitle="Manage your marketplace and store integrations"
                />
                <>
                {gmcStatus && (
                    <Alert
                        severity={gmcStatus === 'connected' ? 'success' : gmcStatus === 'multiple_merchants' ? 'info' : 'error'}
                        onClose={() => setSearchParams({})}
                        sx={{ marginBottom: 2 }}
                    >
                        {gmcStatus === 'connected' && 'Google Merchant Center connected successfully.'}
                        {gmcStatus === 'denied' && 'Google Merchant authorization was cancelled.'}
                        {gmcStatus === 'no_merchant' && 'No Merchant Center account found. Make sure you have a Google Merchant Center account set up.'}
                        {gmcStatus === 'multiple_merchants' && 'Connected — multiple Merchant Center accounts found on your Google account. Enter the Merchant ID you want to use below.'}
                        {gmcStatus === 'error' && 'Something went wrong connecting Google Merchant. Please try again.'}
                    </Alert>
                )}
                {gmcConnectError && (
                    <Alert severity="error" onClose={() => setGmcConnectError(null)} sx={{ marginBottom: 2 }}>
                        {gmcConnectError}
                    </Alert>
                )}
                {etsyStatus && (
                    <Alert
                        severity={etsyStatus === 'connected' ? 'success' : 'error'}
                        onClose={() => setSearchParams({})}
                        sx={{ marginBottom: 2 }}
                    >
                        {etsyStatus === 'connected' && 'Etsy connected successfully.'}
                        {etsyStatus === 'denied' && 'Etsy authorization was cancelled.'}
                        {etsyStatus === 'error' && 'Something went wrong connecting Etsy. Please try again.'}
                    </Alert>
                )}
                {etsyConnectError && (
                    <Alert severity="error" onClose={() => setEtsyConnectError(null)} sx={{ marginBottom: 2 }}>
                        {etsyConnectError}
                    </Alert>
                )}
                {destinations.map((destination, index) => (
                    <React.Fragment key={index}>
                    {( destination.type === 1 ) && (
                    <Accordion defaultExpanded disableGutters sx={{ borderRadius: '12px !important', border: '1px solid #ECEDF3', boxShadow: 'none', mb: 2, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                            <ChannelAvatar channel="shopify" />
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography sx={{ fontWeight: 600 }}>Shopify Integration {destination.storeId ? `— ${destination.storeId}` : ''}</Typography>
                                <ConnectedChip connected={!!destination.storeKey} />
                            </Box>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                        <TextField
                            label="Store ID (name or ID from Shopify (<storeId>.myshopify.com))"
                            variant="outlined"
                            fullWidth
                            value={destination.storeId}
                            onChange={(e) =>
                            handleDestinationChange(index, "storeId", e.target.value)
                            }
                        />
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <ApiKeyField apiKey={destination.storeKey} label='Store Key' onChange={(e) =>
                            handleDestinationChange(index, "storeKey", e.target.value)} readOnly={false}></ApiKeyField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                            <Checkbox
                                checked={destination.autoPostEnabled}
                                onChange={(e) =>
                                handleDestinationChange(index, "autoPostEnabled", e.target.checked)
                                }
                            />
                            }
                            label="Auto post items (Only select if you don't plan on editing AI generated info at all)"
                        />
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                            <Checkbox
                                checked={destination.postToDraft}
                                onChange={(e) =>
                                handleDestinationChange(index, "postToDraft", e.target.checked)
                                }
                            />
                            }
                            label="Post to Draft"
                        />
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <Button
                            variant="outlined"
                            startIcon={<DeleteOutlineIcon />}
                            sx={dangerOutlinePillSx}
                            onClick={() => removeDestination(index)}>
                            Remove this store
                        </Button>
                        </Grid>
                    </Grid>
                    </AccordionDetails>
                    </Accordion>
                    )}
                    {( destination.type === 2 ) && (
                    <Accordion defaultExpanded disableGutters sx={{ borderRadius: '12px !important', border: '1px solid #ECEDF3', boxShadow: 'none', mb: 2, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                            <ChannelAvatar channel="ebay" />
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography sx={{ fontWeight: 600 }}>eBay Integration</Typography>
                                <ConnectedChip connected={!!destination.storeKey} />
                            </Box>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                    <Grid container spacing={3}>
                        {!destination.storeKey && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                Click below to authorize with eBay
                            </Typography>
                        </Grid>
                        )}
                        <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                            <Checkbox
                                checked={destination.autoPostEnabled}
                                onChange={(e) =>
                                handleDestinationChange(index, "autoPostEnabled", e.target.checked)
                                }
                            />
                            }
                            label="Auto post items (Only select if you don't plan on editing AI generated info at all)"
                        />
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                            <Checkbox
                                checked={destination.postToDraft}
                                onChange={(e) =>
                                handleDestinationChange(index, "postToDraft", e.target.checked)
                                }
                            />
                            }
                            label="Post to Draft"
                        />
                        </Grid>
                        <Grid item xs={12}>
                        <Button
                            variant="contained"
                            sx={{ ...primaryPillSx, width: '100%' }}
                            onClick={() => window.location.href = getEbayAuthUrl(customer.customerId, destination.id)}>
                            {destination.storeKey ? 'Reconnect eBay Account' : 'Connect eBay Account'}
                        </Button>
                        </Grid>
                        <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            startIcon={<DeleteOutlineIcon />}
                            sx={{ ...dangerOutlinePillSx, width: '100%' }}
                            onClick={() => removeDestination(index)}>
                            Remove eBay Integration
                        </Button>
                        </Grid>
                        {destination.storeKey && (() => {
                            const options = ebaySettingsOptions[destination.id] || {};
                            const settings = destination.ebaySettings || {};
                            return (
                            <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom style={{ marginTop: 8 }}>
                                eBay Listing Settings
                            </Typography>
                            <Typography variant="body2" style={{ color: '#666', marginBottom: 8 }}>
                                Required before you can post to eBay — eBay needs a default shipping
                                (fulfillment), payment, and return policy, plus an inventory location,
                                for every listing.
                            </Typography>
                            {!options.loaded && (
                                <Button
                                    variant="outlined"
                                    disabled={options.loading}
                                    sx={{ ...outlinePillSx, marginBottom: 2 }}
                                    onClick={() => loadEbaySettingsOptions(destination.id)}>
                                    {options.loading ? 'Loading…' : 'Load eBay Listing Settings'}
                                </Button>
                            )}
                            {options.error && (
                                <Alert severity="error" style={{ marginBottom: 8 }}>{options.error}</Alert>
                            )}
                            {options.loaded && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth disabled={options.fulfillmentPolicies.length === 0}>
                                            <InputLabel>Fulfillment (Shipping) Policy</InputLabel>
                                            <Select
                                                label="Fulfillment (Shipping) Policy"
                                                value={settings.fulfillmentPolicyId || ''}
                                                onChange={(e) => updateEbaySetting(index, 'fulfillmentPolicyId', e.target.value)}>
                                                {options.fulfillmentPolicies.map((p) => (
                                                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {options.fulfillmentPolicies.length === 0 && (
                                            <Typography variant="caption" style={{ color: '#999' }}>
                                                No fulfillment policies found — add one at ebay.com/bp/manage.
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth disabled={options.paymentPolicies.length === 0}>
                                            <InputLabel>Payment Policy</InputLabel>
                                            <Select
                                                label="Payment Policy"
                                                value={settings.paymentPolicyId || ''}
                                                onChange={(e) => updateEbaySetting(index, 'paymentPolicyId', e.target.value)}>
                                                {options.paymentPolicies.map((p) => (
                                                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {options.paymentPolicies.length === 0 && (
                                            <Typography variant="caption" style={{ color: '#999' }}>
                                                No payment policies found — add one at ebay.com/bp/manage.
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth disabled={options.returnPolicies.length === 0}>
                                            <InputLabel>Return Policy</InputLabel>
                                            <Select
                                                label="Return Policy"
                                                value={settings.returnPolicyId || ''}
                                                onChange={(e) => updateEbaySetting(index, 'returnPolicyId', e.target.value)}>
                                                {options.returnPolicies.map((p) => (
                                                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {options.returnPolicies.length === 0 && (
                                            <Typography variant="caption" style={{ color: '#999' }}>
                                                No return policies found — add one at ebay.com/bp/manage.
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth disabled={options.locations.length === 0}>
                                            <InputLabel>Inventory Location</InputLabel>
                                            <Select
                                                label="Inventory Location"
                                                value={settings.merchantLocationKey || ''}
                                                onChange={(e) => updateEbaySetting(index, 'merchantLocationKey', e.target.value)}>
                                                {options.locations.map((l) => (
                                                    <MenuItem key={l.key} value={l.key}>{l.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {options.locations.length === 0 && (
                                            <Typography variant="caption" style={{ color: '#999' }}>
                                                No inventory locations found — add one at ebay.com/bp/manage.
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" style={{ color: '#999' }}>
                                            Don't forget to hit Save at the bottom of the page after choosing your settings.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            )}
                            </Grid>
                            );
                        })()}
                        {destination.storeKey && (
                        <Grid item xs={12}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                            variant="outlined"
                            disabled={catalogLoading}
                            sx={outlinePillSx}
                            onClick={() => openCatalogDialog('ebay')}>
                            {catalogLoading && catalogChannel === 'ebay' ? 'Loading…' : 'Import from eBay'}
                        </Button>
                        <Button
                            variant="outlined"
                            disabled={ebayLinking}
                            sx={outlinePillSx}
                            onClick={handleLinkEbaySkus}>
                            {ebayLinking ? 'Linking…' : 'Link Existing eBay Listings by SKU'}
                        </Button>
                        </Stack>
                        <Typography variant="body2" style={{ marginTop: 8, color: '#666' }}>
                            "Import from eBay" creates new Shopik products from your eBay listings.
                            "Link Existing" instead matches eBay listings to Shopik products that
                            already have the same SKU but aren't linked yet, without creating
                            duplicates. Neither creates or changes anything on eBay itself.
                        </Typography>
                        {ebayLinkResult && (
                            ebayLinkResult.success ? (
                                <Typography variant="body2" style={{ color: 'green', marginTop: 8 }}>
                                    Linked {ebayLinkResult.linked.length} product{ebayLinkResult.linked.length === 1 ? '' : 's'} to existing eBay listings.
                                </Typography>
                            ) : (
                                <Alert severity="error" style={{ marginTop: 8 }}>{ebayLinkResult.error}</Alert>
                            )
                        )}
                        </Grid>
                        )}
                    </Grid>
                    </AccordionDetails>
                    </Accordion>
                    )}
                    {( destination.type === 3 ) && (
                    <Accordion defaultExpanded disableGutters sx={{ borderRadius: '12px !important', border: '1px solid #ECEDF3', boxShadow: 'none', mb: 2, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                            <ChannelAvatar channel="google" />
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography sx={{ fontWeight: 600 }}>Google Merchant Center</Typography>
                                <ConnectedChip connected={!!destination.storeKey} />
                            </Box>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                    <Grid container spacing={3}>
                        {destination.storeKey && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Merchant ID"
                                variant="outlined"
                                fullWidth
                                helperText="Find this in the top-right corner of your Google Merchant Center dashboard"
                                value={destination.storeId}
                                onChange={(e) => handleDestinationChange(index, 'storeId', e.target.value)}
                            />
                        </Grid>
                        )}
                        <Grid item xs={12} md={6}>
                        <Button
                            variant="contained"
                            disabled={gmcConnecting}
                            sx={primaryPillSx}
                            onClick={connectGoogleMerchant}>
                            {gmcConnecting ? 'Connecting…' : destination.storeKey ? 'Reconnect Google Merchant' : 'Connect Google Merchant'}
                        </Button>
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                            <Checkbox
                                checked={destination.autoPostEnabled}
                                onChange={(e) =>
                                handleDestinationChange(index, "autoPostEnabled", e.target.checked)
                                }
                            />
                            }
                            label="Auto post items"
                        />
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <Button
                            variant="outlined"
                            startIcon={<DeleteOutlineIcon />}
                            sx={dangerOutlinePillSx}
                            onClick={() => removeDestination(index)}>
                            Remove Google Merchant
                        </Button>
                        </Grid>
                    </Grid>
                    </AccordionDetails>
                    </Accordion>
                    )}
                    {( destination.type === 5 ) && (
                    <Accordion defaultExpanded disableGutters sx={{ borderRadius: '12px !important', border: '1px solid #ECEDF3', boxShadow: 'none', mb: 2, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                            <ChannelAvatar channel="etsy" />
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography sx={{ fontWeight: 600 }}>Etsy Integration</Typography>
                                <ConnectedChip connected={!!destination.storeKey} />
                            </Box>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                    <Grid container spacing={3}>
                        {destination.storeKey && destination.storeId && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                Shop ID {destination.storeId}
                            </Typography>
                        </Grid>
                        )}
                        {!destination.storeKey && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                Click below to authorize with Etsy
                            </Typography>
                        </Grid>
                        )}
                        <Grid item xs={12} md={6}>
                        <Button
                            variant="contained"
                            disabled={etsyConnecting}
                            sx={primaryPillSx}
                            onClick={connectEtsy}>
                            {etsyConnecting ? 'Connecting…' : destination.storeKey ? 'Reconnect Etsy Account' : 'Connect Etsy Account'}
                        </Button>
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                            <Checkbox
                                checked={destination.autoPostEnabled}
                                onChange={(e) =>
                                handleDestinationChange(index, "autoPostEnabled", e.target.checked)
                                }
                            />
                            }
                            label="Auto post items (Only select if you don't plan on editing AI generated info at all)"
                        />
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                            <Checkbox
                                checked={destination.postToDraft}
                                onChange={(e) =>
                                handleDestinationChange(index, "postToDraft", e.target.checked)
                                }
                            />
                            }
                            label="Post to Draft"
                        />
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <Button
                            variant="outlined"
                            startIcon={<DeleteOutlineIcon />}
                            sx={dangerOutlinePillSx}
                            onClick={() => removeDestination(index)}>
                            Remove Etsy Integration
                        </Button>
                        </Grid>
                        {destination.storeKey && (() => {
                            const options = etsySettingsOptions[destination.id] || {};
                            const taxonomy = etsyTaxonomySearch[destination.id] || {};
                            const settings = destination.etsySettings || {};
                            return (
                            <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom style={{ marginTop: 8 }}>
                                Etsy Listing Settings
                            </Typography>
                            <Typography variant="body2" style={{ color: '#666', marginBottom: 8 }}>
                                Required before you can post to Etsy — Etsy needs a default shipping
                                profile, return policy, and category (taxonomy) for every listing.
                            </Typography>
                            {!options.loaded && (
                                <Button
                                    variant="outlined"
                                    disabled={options.loading}
                                    sx={{ ...outlinePillSx, marginBottom: 2 }}
                                    onClick={() => loadEtsySettingsOptions(destination.id)}>
                                    {options.loading ? 'Loading…' : 'Load Etsy Listing Settings'}
                                </Button>
                            )}
                            {options.error && (
                                <Alert severity="error" style={{ marginBottom: 8 }}>{options.error}</Alert>
                            )}
                            {options.loaded && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth disabled={options.shippingProfiles.length === 0}>
                                            <InputLabel>Shipping Profile</InputLabel>
                                            <Select
                                                label="Shipping Profile"
                                                value={settings.shippingProfileId || ''}
                                                onChange={(e) => updateEtsySetting(index, 'shippingProfileId', e.target.value)}>
                                                {options.shippingProfiles.map((p) => (
                                                    <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {options.shippingProfiles.length === 0 && (
                                            <Typography variant="caption" style={{ color: '#999' }}>
                                                No shipping profiles found — add one at etsy.com/your/shops/me/shipping.
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth disabled={options.returnPolicies.length === 0}>
                                            <InputLabel>Return Policy</InputLabel>
                                            <Select
                                                label="Return Policy"
                                                value={settings.returnPolicyId || ''}
                                                onChange={(e) => updateEtsySetting(index, 'returnPolicyId', e.target.value)}>
                                                {options.returnPolicies.map((p) => (
                                                    <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {options.returnPolicies.length === 0 && (
                                            <Typography variant="caption" style={{ color: '#999' }}>
                                                No return policies found — add one at etsy.com/your/shops/me/policies.
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Who Made It</InputLabel>
                                            <Select
                                                label="Who Made It"
                                                value={settings.whoMade || 'i_did'}
                                                onChange={(e) => updateEtsySetting(index, 'whoMade', e.target.value)}>
                                                {ETSY_WHO_MADE_OPTIONS.map((o) => (
                                                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>When Made</InputLabel>
                                            <Select
                                                label="When Made"
                                                value={settings.whenMade || 'made_to_order'}
                                                onChange={(e) => updateEtsySetting(index, 'whenMade', e.target.value)}>
                                                {ETSY_WHEN_MADE_OPTIONS.map((o) => (
                                                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            freeSolo
                                            options={taxonomy.results || []}
                                            getOptionLabel={(o) => (typeof o === 'string' ? o : o.path)}
                                            loading={!!taxonomy.loading}
                                            filterOptions={(x) => x}
                                            value={settings.taxonomyPath ? { id: settings.taxonomyId, path: settings.taxonomyPath } : null}
                                            onInputChange={(e, value, reason) => {
                                                if (reason === 'input') searchEtsyTaxonomyFor(destination.id, value);
                                            }}
                                            onChange={(e, selected) => {
                                                if (selected && typeof selected !== 'string') {
                                                    updateEtsySetting(index, 'taxonomyId', selected.id);
                                                    updateEtsySetting(index, 'taxonomyPath', selected.path);
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Default Category (Taxonomy)"
                                                    helperText="Search Etsy's category tree, e.g. 'jewelry' or 'clothing'." />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" style={{ color: '#999' }}>
                                            Don't forget to hit Save at the bottom of the page after choosing your settings.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            )}
                            </Grid>
                            );
                        })()}
                    </Grid>
                    </AccordionDetails>
                    </Accordion>
                    )}
                    </React.Fragment>
                ))}
                {squareSettings && (
                    <Accordion defaultExpanded disableGutters sx={{ borderRadius: '12px !important', border: '1px solid #ECEDF3', boxShadow: 'none', mb: 2, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                            <ChannelAvatar channel="square" />
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography sx={{ fontWeight: 600 }}>Square Integration</Typography>
                                <ConnectedChip connected={!!squareSettings.accessToken} />
                            </Box>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
                            Find your credentials in the{' '}
                            <a href="https://developer.squareup.com/apps" target="_blank" rel="noopener noreferrer">
                                Square Developer Dashboard
                            </a>
                            . Open your app → Credentials tab → copy the <strong>Production Access Token</strong> and <strong>Location ID</strong> (found under Locations).
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <ApiKeyField
                                    apiKey={squareSettings.accessToken || ''}
                                    label="Access Token"
                                    onChange={(e) => {
                                        setSquareSettings((prev) => ({ ...prev, accessToken: e.target.value }));
                                        setDataChanged(true);
                                    }}
                                    readOnly={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Location ID"
                                    variant="outlined"
                                    fullWidth
                                    value={squareSettings.locationId || ''}
                                    onChange={(e) => {
                                        setSquareSettings((prev) => ({ ...prev, locationId: e.target.value }));
                                        setDataChanged(true);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                    <Checkbox
                                        checked={!!squareDestination?.autoPostEnabled}
                                        onChange={(e) =>
                                        handleSquareDestinationChange("autoPostEnabled", e.target.checked)
                                        }
                                    />
                                    }
                                    label="Auto post items to Square (off by default — Square only imports until enabled)"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                    variant="outlined"
                                    sx={outlinePillSx}
                                    disabled={catalogLoading}
                                    onClick={() => openCatalogDialog('square')}>
                                    {catalogLoading && catalogChannel === 'square' ? 'Loading…' : 'Import from Square'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<DeleteOutlineIcon />}
                                    sx={dangerOutlinePillSx}
                                    onClick={() => {
                                        setSquareSettings(null);
                                        setDestinations((prev) => prev.filter(d => d.type !== 4));
                                        setDataChanged(true);
                                    }}>
                                    Remove Square Integration
                                </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                    </Accordion>
                )}

                {/* Add More Integrations — icon grid of the channels not yet connected */}
                {(!destinations.some(d => d.type === 1) || !destinations.some(d => d.type === 2) || !destinations.some(d => d.type === 3) || !destinations.some(d => d.type === 5) || !squareSettings) && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Add More Integrations
                    </Typography>
                    <Grid container spacing={1.5}>
                        {/* Only one Shopify store per customer is supported for now. */}
                        {!destinations.some(d => d.type === 1) && (
                        <Grid item xs={6} sm={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<StorefrontIcon sx={{ color: CHANNEL_META.shopify.color }} />}
                                sx={{ ...outlinePillSx, borderRadius: 2, justifyContent: 'flex-start', py: 1.2 }}
                                onClick={addNewDestination}>
                                Shopify Store
                            </Button>
                        </Grid>
                        )}
                        {!destinations.some(d => d.type === 2) && (
                        <Grid item xs={6} sm={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<SellIcon sx={{ color: CHANNEL_META.ebay.color }} />}
                                sx={{ ...outlinePillSx, borderRadius: 2, justifyContent: 'flex-start', py: 1.2 }}
                                onClick={addEbayIntegration}>
                                eBay
                            </Button>
                        </Grid>
                        )}
                        {!destinations.some(d => d.type === 3) && (
                        <Grid item xs={6} sm={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                disabled={gmcConnecting}
                                startIcon={<ShoppingBagIcon sx={{ color: CHANNEL_META.google.color }} />}
                                sx={{ ...outlinePillSx, borderRadius: 2, justifyContent: 'flex-start', py: 1.2 }}
                                onClick={connectGoogleMerchant}>
                                {gmcConnecting ? 'Connecting…' : 'Google Merchant'}
                            </Button>
                        </Grid>
                        )}
                        {!destinations.some(d => d.type === 5) && (
                        <Grid item xs={6} sm={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                disabled={etsyConnecting}
                                startIcon={<LocalOfferIcon sx={{ color: CHANNEL_META.etsy.color }} />}
                                sx={{ ...outlinePillSx, borderRadius: 2, justifyContent: 'flex-start', py: 1.2 }}
                                onClick={connectEtsy}>
                                {etsyConnecting ? 'Connecting…' : 'Etsy'}
                            </Button>
                        </Grid>
                        )}
                        {!squareSettings && (
                        <Grid item xs={6} sm={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<CropSquareIcon sx={{ color: CHANNEL_META.square.color }} />}
                                sx={{ ...outlinePillSx, borderRadius: 2, justifyContent: 'flex-start', py: 1.2 }}
                                onClick={() => {
                                    setSquareSettings({ accessToken: '', locationId: '' });
                                    setDataChanged(true);
                                }}>
                                Square
                            </Button>
                        </Grid>
                        )}
                    </Grid>
                </Box>
                )}

                {(squareSettings || destinations.length > 0) && (
                    <Paper style={{ ...sectionPaperSx, marginTop: 20, marginBottom: 0 }} variant="outlined">
                        <SectionHeader icon={<SyncIcon />} title="Inventory Sync" />
                        <Typography variant="body2" color="text.secondary" style={{ marginBottom: 12, marginTop: -12 }}>
                            Automatically poll your connected channels every 5 minutes for new sales.
                            When a product sells, its quantity is decremented on Shopik and it is
                            delisted from all other channels when stock reaches zero.
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!customerData.inventorySyncEnabled}
                                    onChange={(e) => {
                                        setCustomerData((prev) => ({ ...prev, inventorySyncEnabled: e.target.checked }));
                                        setDataChanged(true);
                                    }}
                                />
                            }
                            label="Enable cross-channel inventory sync"
                        />
                    </Paper>
                )}
                {!!shopikUser.isSuperAdmin && (
                    <Paper style={{ ...sectionPaperSx, marginTop: 20, marginBottom: 0 }} variant="outlined">
                        <SectionHeader icon={<SyncIcon />} title="Admin" subtitle="Internal — not shown to regular customers." />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!customerData.useExperimentalFeatures}
                                    onChange={(e) => {
                                        setCustomerData((prev) => ({ ...prev, useExperimentalFeatures: e.target.checked }));
                                        setDataChanged(true);
                                    }}
                                />
                            }
                            label="Use experimental pipeline"
                        />
                    </Paper>
                )}
                </>
            </Paper>
            </>
            )}
            {(customer.customerId === shopikUser.userId) && (
            <Paper style={sectionPaperSx}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Custom SKU Format"
                            variant="outlined"
                            fullWidth
                            value={customerData.customSkuFormat}
                            onChange={handleCustomerDataChange}
                        />
                    </Grid>
                </Grid>
            </Paper>
            )}
            <Paper style={sectionPaperSx}>
                <SectionHeader icon={<BarChartIcon />} title="Usage" subtitle="View your usage and billing details" />
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Button
                    variant="outlined"
                    sx={outlinePillSx}
                    onClick={() => navigate('/dashboard')}
                >
                    View Usage
                </Button>
                {/* Hidden for now: the Stripe portal still shows legacy plans. Re-enable by removing `false &&`. */}
                {false && customer.stripeCustomerId && (
                    <Button
                        variant="outlined"
                        sx={outlinePillSx}
                        disabled={manageSubscriptionLoading}
                        onClick={manageSubscription}
                    >
                        {manageSubscriptionLoading ? 'Loading…' : 'Manage Subscription'}
                    </Button>
                )}
                </Stack>
                {customer.tierSubscription && ['trialing', 'active'].includes(customer.tierSubscription.status) && (
                    customer.tierSubscription.cancelAtPeriodEnd ? (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Your subscription is set to cancel{
                                (customer.tierSubscription.currentPeriodEnd || customer.tierSubscription.trialEndDate)
                                    ? ` on ${formattedDate(customer.tierSubscription.currentPeriodEnd || customer.tierSubscription.trialEndDate)}`
                                    : ''
                            }.
                        </Alert>
                    ) : (
                        <Button
                            variant="outlined"
                            color="error"
                            sx={{ textTransform: 'none' }}
                            onClick={() => setCancelDialogOpen(true)}
                        >
                            Cancel Subscription
                        </Button>
                    )
                )}
                {cancelSuccess && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        Your subscription has been scheduled to cancel. You'll keep access until the end of your current period.
                    </Alert>
                )}
            </Paper>

            <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
                <DialogTitle>Cancel your subscription?</DialogTitle>
                <DialogContent>
                    <Typography>
                        {customer?.tierSubscription?.status === 'trialing'
                            ? "You won't be charged, and your access will end when your trial period ends."
                            : 'Your access will continue until the end of your current billing period, and you will not be charged again.'}
                    </Typography>
                    {cancelError && (
                        <Alert severity="error" sx={{ mt: 2 }}>{cancelError}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelLoading}>
                        Keep my subscription
                    </Button>
                    <Button color="error" variant="contained" onClick={cancelSubscription} disabled={cancelLoading}>
                        {cancelLoading ? 'Canceling…' : 'Yes, cancel'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Paper style={sectionPaperSx}>
                <SectionHeader icon={<SupportAgentIcon />} title="Support" />
                <Button
                    variant="outlined"
                    sx={outlinePillSx}
                    href="mailto:support@shopik.io"
                >
                    Email Support
                </Button>
            </Paper>
        </Container>

        {/* Floating save/cancel bar — fixed so it's always reachable without scrolling
            back down after editing a field anywhere on the page (e.g. a Square key). */}
        {dataChanged && (
            <Paper
                elevation={6}
                sx={{
                    position: 'fixed',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1300,
                    padding: '10px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderRadius: 2,
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    You have unsaved changes
                </Typography>
                <Button
                    variant="outlined"
                    sx={outlinePillSx}
                    onClick={cancelSave}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    sx={primaryPillSx}
                    onClick={saveUserData}
                >
                    Save
                </Button>
            </Paper>
        )}

        {/* Generic channel catalog selection dialog — reused for Square, eBay, and any future channel */}
        <Dialog open={catalogOpen} onClose={() => setCatalogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Import from {CATALOG_CHANNEL_LABELS[catalogChannel] || catalogChannel}</DialogTitle>
            <DialogContent sx={{ pb: 0, pt: 1.5 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search items…"
                    value={catalogSearchInput}
                    onChange={e => setCatalogSearchInput(e.target.value)}
                    inputProps={{ autoComplete: 'off' }}
                />
            </DialogContent>
            <DialogContent dividers sx={{ p: 0 }}>
                {catalogLoading && (
                    <Grid container justifyContent="center" sx={{ py: 4 }}>
                        <CircularProgress />
                    </Grid>
                )}
                {catalogError && (
                    <Typography color="error" sx={{ p: 2 }}>{catalogError}</Typography>
                )}
                {!catalogLoading && !catalogError && catalogItems.length === 0 && (
                    <Typography sx={{ p: 2 }} color="text.secondary">
                        No items found in {CATALOG_CHANNEL_LABELS[catalogChannel] || catalogChannel} catalog.
                    </Typography>
                )}
                {!catalogLoading && catalogItems.length > 0 && (
                    <>
                        <Grid container alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                {selectedItemIds.size > 0 ? `${selectedItemIds.size} selected` : 'Select items to import'}
                            </Typography>
                            <Grid container alignItems="center" gap={1} sx={{ width: 'auto' }}>
                                <Button size="small" onClick={() => {
                                    const available = catalogItems.filter(i => !i.already_imported && i.listing_status !== 'unpublished').map(i => i.external_id);
                                    const allSelected = available.every(id => selectedItemIds.has(id));
                                    setSelectedItemIds(prev => {
                                        const next = new Set(prev);
                                        if (allSelected) {
                                            available.forEach(id => next.delete(id));
                                        } else {
                                            available.forEach(id => next.add(id));
                                        }
                                        return next;
                                    });
                                }}>
                                    {catalogItems.filter(i => !i.already_imported && i.listing_status !== 'unpublished').every(i => selectedItemIds.has(i.external_id)) ? 'Deselect page' : 'Select page'}
                                </Button>
                                <FormControl size="small" sx={{ minWidth: 80 }}>
                                    <Select
                                        value={catalogPageSize}
                                        onChange={e => {
                                            const newSize = e.target.value;
                                            setCatalogPageSize(newSize);
                                            setCatalogCursorStack([]);
                                            loadCatalogPage(null, newSize, catalogApiKey);
                                        }}
                                        displayEmpty
                                        variant="outlined"
                                        sx={{ fontSize: '0.8rem' }}
                                    >
                                        {[10, 25, 50].map(n => (
                                            <MenuItem key={n} value={n}>{n} / page</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <List dense disablePadding>
                            {catalogItems.map(item => {
                                const unpublished = item.listing_status === 'unpublished';
                                const disabled = item.already_imported || unpublished;
                                const checked = selectedItemIds.has(item.external_id);
                                return (
                                    <ListItem
                                        key={item.external_id}
                                        button
                                        disabled={disabled}
                                        onClick={() => {
                                            if (disabled) return;
                                            setSelectedItemIds(prev => {
                                                const next = new Set(prev);
                                                if (next.has(item.external_id)) next.delete(item.external_id);
                                                else next.add(item.external_id);
                                                return next;
                                            });
                                        }}
                                        sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: checked ? 'action.selected' : 'inherit' }}
                                    >
                                        <Checkbox checked={checked} disabled={disabled} size="small" sx={{ mr: 1 }} tabIndex={-1} disableRipple />
                                        <ListItemAvatar>
                                            <Avatar
                                                src={item.image_urls?.[0] || ''}
                                                variant="rounded"
                                                sx={{ width: 48, height: 48, bgcolor: 'grey.200' }}
                                            />
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={item.name || '(no name)'}
                                            secondary={[
                                                item.price ? `$${item.price}` : null,
                                                item.category_name || null,
                                            ].filter(Boolean).join(' · ')}
                                        />
                                        {item.listing_status === 'scheduled' &&
                                            <Chip label="Scheduled" size="small" color="info" variant="outlined" sx={{ ml: 1 }} />
                                        }
                                        {unpublished &&
                                            <Chip label="Not published" size="small" color="default" variant="outlined" sx={{ ml: 1 }} />
                                        }
                                        {item.already_imported
                                            ? <Chip label="In Hemlock" size="small" color="success" variant="outlined" sx={{ ml: 1 }} />
                                            : item.image_urls?.length > 0 && <Chip label={`${item.image_urls.length} img`} size="small" sx={{ ml: 1 }} />
                                        }
                                    </ListItem>
                                );
                            })}
                        </List>
                        {/* Pagination controls */}
                        {(catalogCursorStack.length > 0 || catalogHasMore) && (
                            <Grid container alignItems="center" justifyContent="center" gap={1} sx={{ py: 1.5 }}>
                                {catalogTotal > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%', textAlign: 'center' }}>
                                        Showing {catalogRangeStart}-{catalogRangeEnd} of {catalogTotal}
                                    </Typography>
                                )}
                                <Button
                                    size="small"
                                    disabled={catalogCursorStack.length === 0}
                                    onClick={() => {
                                        const stack = [...catalogCursorStack];
                                        const prevCursor = stack.pop() ?? null;
                                        setCatalogCursorStack(stack);
                                        loadCatalogPage(prevCursor, catalogPageSize, catalogApiKey);
                                    }}
                                >
                                    ← Prev
                                </Button>
                                <Typography variant="body2" color="text.secondary">
                                    Page {catalogCursorStack.length + 1}
                                </Typography>
                                <Button
                                    size="small"
                                    disabled={!catalogHasMore}
                                    onClick={() => {
                                        setCatalogCursorStack(prev => [...prev, catalogCurrentCursor]);
                                        loadCatalogPage(catalogNextCursor, catalogPageSize, catalogApiKey);
                                    }}
                                >
                                    Next →
                                </Button>
                            </Grid>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {importResult && (
                    <Typography variant="body2" color={importResult.error ? 'error' : 'success.main'} sx={{ flex: 1, ml: 1 }}>
                        {importResult.error
                            ? `Failed: ${importResult.error}`
                            : `Imported ${importResult.imported}.${importResult.skipped_existing ? ` ${importResult.skipped_existing} already in Shopik.` : ''}${importResult.errors?.length ? ` ${importResult.errors.length} errors: ${importResult.errors.join('; ')}` : ''}`}
                    </Typography>
                )}
                <Button onClick={() => setCatalogOpen(false)}>Close</Button>
                <Button
                    variant="contained"
                    disabled={importing || selectedItemIds.size === 0}
                    onClick={async () => {
                        setImporting(true);
                        setImportResult(null);
                        try {
                            const key = catalogApiKey ?? await getApiKey(customer.customerId);
                            const result = await importChannelCatalog(catalogChannel, key, Array.from(selectedItemIds), catalogCurrentCursor);
                            setImportResult(result);
                            setSelectedItemIds(new Set());
                            loadCatalogPage(catalogCurrentCursor, catalogPageSize, key);
                        } catch (e) {
                            setImportResult({ error: e.message });
                        } finally {
                            setImporting(false);
                        }
                    }}
                >
                    {importing ? 'Importing…' : `Import ${selectedItemIds.size > 0 ? `(${selectedItemIds.size})` : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default Settings;